import { minutesFromTime, timeFromIso } from "./date.js";

function localDateOnly(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

export function classifyAttendance({ date, punchIn, punchOut, weeklyOffDay, shift }) {
  const dayName = new Intl.DateTimeFormat("en-US", { weekday: "long", timeZone: "UTC" }).format(
    new Date(`${date}T00:00:00.000Z`)
  );
  const isWeeklyOffDate = Boolean(weeklyOffDay && dayName === weeklyOffDay);

  if (!punchIn && !punchOut) {
    return {
      code: isWeeklyOffDate ? "WO" : "A",
      status: isWeeklyOffDate ? "Weekly Off" : "Absent",
      presentUnits: 0,
      absentUnits: isWeeklyOffDate ? 0 : 1,
      late: false,
      earlyDeparture: false,
      isWeeklyOffDate
    };
  }

  if (!punchIn || !punchOut) {
    return {
      code: "M",
      status: "Missing punch",
      presentUnits: 0,
      absentUnits: 1,
      late: false,
      earlyDeparture: false,
      isWeeklyOffDate
    };
  }

  const punchInMinutes = minutesFromTime(timeFromIso(punchIn.timestamp || punchIn.createdAt));
  const punchOutMinutes = minutesFromTime(timeFromIso(punchOut.timestamp || punchOut.createdAt));
  const shiftStart = minutesFromTime(shift?.startTime || shift?.start_time || "11:00");
  const graceMinutes = Number(shift?.graceMinutes ?? shift?.grace_minutes ?? 30);
  const fullDayOutBy = minutesFromTime(shift?.fullDayOutBy || shift?.full_day_out_by || "21:00");
  const halfDayCutoff = minutesFromTime(shift?.halfDayCutoff || shift?.half_day_cutoff || "15:00");
  const latestOnTime = shiftStart + graceMinutes;

  const onTimeArrival = punchInMinutes <= latestOnTime;
  const lateArrivalHalf = punchInMinutes >= halfDayCutoff;
  const fullDeparture = punchOutMinutes >= fullDayOutBy;
  const partialDeparture = punchOutMinutes >= halfDayCutoff && punchOutMinutes < fullDayOutBy;

  let code = "A";
  let status = "Absent";
  let presentUnits = 0;
  let absentUnits = 1;

  if (onTimeArrival && fullDeparture) {
    code = isWeeklyOffDate ? "P*" : "P";
    status = isWeeklyOffDate ? "Present on weekly off" : "Present";
    presentUnits = 1;
    absentUnits = 0;
  } else if ((onTimeArrival && partialDeparture) || (lateArrivalHalf && fullDeparture)) {
    code = "HD";
    status = "Half Day";
    presentUnits = 0.5;
    absentUnits = 0.5;
  }

  return {
    code,
    status,
    presentUnits,
    absentUnits,
    late: punchInMinutes > latestOnTime,
    earlyDeparture: punchOutMinutes < fullDayOutBy,
    isWeeklyOffDate
  };
}

export function buildDailyAttendance({ date, punches, weeklyOffDay, shift }) {
  const dayPunches = punches
    .filter((punch) => (punch.date || localDateOnly(punch.timestamp || punch.createdAt)) === date)
    .sort((a, b) => String(a.timestamp || a.createdAt).localeCompare(String(b.timestamp || b.createdAt)));

  const punchIn = dayPunches.find((punch) => punch.type === "in");
  const punchOut = [...dayPunches].reverse().find((punch) => punch.type === "out");

  return {
    date,
    punchIn,
    punchOut,
    ...classifyAttendance({ date, punchIn, punchOut, weeklyOffDay, shift })
  };
}
